//Libraries
import { Card, Flex, List, Text } from '@chakra-ui/react';
import { ReactElement } from 'react';
// Components
import { DataStateWrapper } from '@molecules/index';
// Utils
import { formatNumberToDecimal } from '@/src/utils/helpers';
import text from '@translations/en.json';
// Types
import { DailyAveragesProps, Factor } from './DailyAverages.type';

/**
 * A dictionary mapping the factors to their corresponding names.
 *
 * @type {Record<Factor, string>}
 */
const dailyAverageDictionary: Record<Factor, string> = {
  foc: text.daily_consumption,
  speed: text.speed,
  displacement: text.displacement,
  beaufort: text.weather,
};

/**
 * Renders a component that displays daily averages.
 *
 * @component
 * @param {DailyAveragesProps} props - The component props.
 * @param {Array<DailyAverage>} props.dailyAverages - The array of daily averages to display.
 * @param {string} [props.width='full'] - The width of the component.
 * @returns {ReactElement} The rendered DailyAverages component.
 */
export const DailyAverages = (props: DailyAveragesProps): ReactElement => {
  const { dailyAverages, width = 'full' } = props;

  const dailyAveragesDisplayed = dailyAverages.filter(({ factor }) => factor != 'displacement');

  const formatValue = (number: number) => (Number.isInteger(number) ? number : formatNumberToDecimal(number, 1));

  const isEmpty = dailyAverages.length === 0;

  return (
    <Card border='greyLight' h='full'>
      <Flex w={width} maxW={113} direction='column' p={4}>
        <Text as='h3' fontSize='xl' fontWeight='semibold'>
          {text.daily_averages}
        </Text>
        <DataStateWrapper isEmpty={isEmpty}>
          <List mt={9}>
            {dailyAveragesDisplayed.map(({ factor, value, units }) => (
              <Flex
                as='li'
                justify='space-between'
                key={factor}
                py={3}
                border='none'
                borderTopStyle='solid'
                borderColor='gray.200'
                borderWidth='1px'
                _first={{ borderTopStyle: 'none' }}
              >
                <Text fontSize='m'>{dailyAverageDictionary[factor]}</Text>
                <Text fontSize='m' color='grey.100'>
                  {`${formatValue(value)} ${units}`}
                </Text>
              </Flex>
            ))}
          </List>
        </DataStateWrapper>
      </Flex>
    </Card>
  );
};
